import { FC, useEffect, useMemo } from 'react';
import { RootStateOrAny, useSelector } from 'react-redux';
import Link from 'next/link';

import EmptyC from 'src/components/Layout/Empty';
import { ICategoryByGroupDetail, IChildCategory } from 'src/interface/CategoryByGroup';
import { trackInsiderObject } from 'src/utils/analytics/insider';
import { truncateString } from 'src/utils/func/format-string';

const AllCategories: FC = () => {
	const categories: ICategoryByGroupDetail[] = useSelector(
		(state: RootStateOrAny) => state.categoryByGroup.all,
	);

	const parentCategories = useMemo(() => {
		if (!categories) {
			return [];
		}
		return categories.filter((category: ICategoryByGroupDetail) => !category.parent_uid);
	}, [categories]);

	useEffect(() => {
		trackInsiderObject('page')({
			type: 'Category',
		});
	}, []);

	if (parentCategories?.length <= 0) {
		return (
			<EmptyC
				image="/images/empty/medical-history.svg"
				title="Kategori tidak ditemukan"
				description="Kategori yang kamu cari belum tersedia, silahkan coba beberapa saat lagi"
				buttonText=""
			/>
		);
	}

	return (
		<div className="container flex flex-col pb-5 w-full">
			{parentCategories.map((parentCategory: ICategoryByGroupDetail) => {
				let linkParent: string = parentCategory.full_slug;
				if (linkParent.substring(0, 1) !== '/') {
					linkParent = `/${linkParent}`;
				}
				return (
					<div key={parentCategory.uid}>
						<h2 className="m-0">
							<Link
								href={linkParent}
								style={{ color: parentCategory.color_text }}
								className="block font-bold text-neutral-3 text-xl lg:text-2xl leading-[3rem] mt-4 mb-[1.625rem] md:mt-[1.3125rem] md:mb-[1.2rem]"
							>
								{parentCategory.name}
							</Link>
						</h2>
						<ul className="p-0 list-none flex flex-wrap justify-start my-[18px] sm:my-6 lg:mt-4 lg:mb-10 gap-x-[30px] gap-y-[15px] lg:gap-y-9">
							{parentCategory.child.map((childCategory: IChildCategory) => {
								let linkRedirect: string = childCategory.full_slug.toLowerCase();
								if (linkRedirect.substring(0, 1) !== '/') {
									linkRedirect = `/${linkRedirect}`;
								}
								return (
									<li
										key={childCategory.uid}
										className="flex bg-neutral-19 bg-[url('/icons/category/ic_all_category.svg')] bg-right bg-contain bg-no-repeat rounded-md w-[130px] h-[66px] sm:w-[210px] sm:h-[98px] xl:w-[255px] xl:h-[116px]"
									>
										<Link
											href={linkRedirect}
											className="h-full w-full text-neutral-5 font-semibold text-left rounded-[10px] flex justify-start items-start cursor-pointer py-3 px-2 sm:px-6 text-xs md:text-base lg:text-2xl xl:text-xl leading-4 md:leading-9 lg:leading-7 hover:bg-primary hover:text-primary-white transition duration-200"
										>
											{truncateString(childCategory.name, 30)}
										</Link>
									</li>
								);
							})}
						</ul>
					</div>
				);
			})}
		</div>
	);
};

export default AllCategories;
